import { useCallback, useState, useEffect } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import {
  Tabs,
  Tab,
  TextField,
  Button,
  Divider,
  Stack,
  CircularProgress,
  Typography,
} from '@mui/material';
import { GenericDialog } from '@/components/shared/GenericDialog';
import { containedButtonStyle, textButtonStyle } from '@/theme';
import { abyssColors } from '@/theme/colors';
import { openTaskModal as openTaskModalState } from '../state/atoms';
import {
  riskAssessmentAssemblyTemplate,
  selectedAssemblyId as selectedAssemblyIdState,
  selectedAssemblyIds as selectedIds,
  unselectedAssemblyIds,
  selectedRiskAssessmentFilters,
} from '@/components/Analysis/state';
import {
  useUpdateRiskAssessmentTemplateFieldDataForAssembliesMutation,
  GetAssembliesByRiskAssessmentFiltersDocument,
  GetVersionsDocument,
  useGetAssemblyForTaskModalQuery,
} from '@/__generated__/graphql';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';

type TaskKey = 'risk_task_description' | 'risk_task_frequency' | 'risk_task_responsibility';

type TaskDataType = Record<TaskKey, string>;

const TABS: { label: string; key: TaskKey }[] = [
  { label: 'Task Description', key: 'risk_task_description' },
  { label: 'Task Frequency', key: 'risk_task_frequency' },
  { label: 'Task Responsibility', key: 'risk_task_responsibility' },
];

type TaskModalProps = {
  updateAllAssemblies: boolean;
};

export const TaskModal = ({ updateAllAssemblies }: TaskModalProps) => {
  const structureId = useGetStructureId();
  const [openTaskModal, setOpenTaskModal] = useAtom(openTaskModalState);
  const selectedAssemblyId = useAtomValue(selectedAssemblyIdState);
  const selectedAssemblyIds = useAtomValue(selectedIds);
  const excludedAssemblyIds = useAtomValue(unselectedAssemblyIds);
  const selectedFilters = useAtomValue(selectedRiskAssessmentFilters);
  const template = useAtomValue(riskAssessmentAssemblyTemplate);
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);

  const [tabIndex, setTabIndex] = useState(0);
  const [taskData, setTaskData] = useState<TaskDataType>({
    risk_task_description: '',
    risk_task_frequency: '',
    risk_task_responsibility: '',
  });
  const [isSaveDisabled, setIsSaveDisabled] = useState(true);

  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });

  const handleTabChange = useCallback((_event: React.SyntheticEvent, newIndex: number) => {
    setTabIndex(newIndex);
  }, []);

  const handleInputChange = useCallback((key: string, value: string) => {
    setIsSaveDisabled(false);
    setTaskData((previousData) => ({
      ...previousData,
      [key]: value,
    }));
  }, []);

  const handleClose = useCallback(() => {
    setOpenTaskModal(false);
    setTabIndex(0);
  }, [setOpenTaskModal]);

  const { data: assemblyTemplateFieldData, loading: taskDataLoading } =
    useGetAssemblyForTaskModalQuery({
      variables: {
        assemblyId: selectedAssemblyId ?? '',
      },
    });

  useEffect(() => {
    if (assemblyTemplateFieldData?.assembly) {
      const { templateFieldData } = assemblyTemplateFieldData.assembly;
      setTaskData({
        risk_task_description: templateFieldData?.risk_task_description ?? '',
        risk_task_frequency: templateFieldData?.risk_task_frequency ?? '',
        risk_task_responsibility: templateFieldData?.risk_task_responsibility ?? '',
      });
    }
  }, [assemblyTemplateFieldData?.assembly]);

  const [updateTaskData, { loading: updatingTaskData }] =
    useUpdateRiskAssessmentTemplateFieldDataForAssembliesMutation({
      refetchQueries: [GetAssembliesByRiskAssessmentFiltersDocument, GetVersionsDocument],
      awaitRefetchQueries: true,
      onCompleted: () => {
        showSnackBar('The task description has been saved successfully', 'success');
        setOpenConfirmationDialog(false);
        setIsSaveDisabled(true);
      },
    });

  const handleSave = useCallback(() => {
    setOpenTaskModal(false);
    setOpenConfirmationDialog(true);
  }, [setOpenTaskModal]);

  const handleUpdateSelectedComponent = useCallback(() => {
    updateTaskData({
      variables: {
        input: {
          structureId,
          templateId: template?.id ?? '',
          selectedFilters,
          assemblyIds: selectedAssemblyId ? [selectedAssemblyId] : [],
          excludedAssemblyIds: [],
          selectedInputs: Object.entries(taskData).map(([key, value]) => ({
            id: key,
            value: value ?? '',
          })),
          updateAllAssemblies: false,
          lastModifiedFor: 'Task Description',
        },
      },
    });
  }, [selectedAssemblyId, selectedFilters, structureId, taskData, template?.id, updateTaskData]);

  const handleUpdateAllSelected = useCallback(() => {
    updateTaskData({
      variables: {
        input: {
          structureId,
          templateId: template?.id ?? '',
          selectedFilters,
          assemblyIds: selectedAssemblyIds,
          excludedAssemblyIds: excludedAssemblyIds,
          selectedInputs: Object.entries(taskData).map(([key, value]) => ({
            id: key,
            value: value ?? '',
          })),
          updateAllAssemblies: updateAllAssemblies,
          lastModifiedFor: 'Task Description',
        },
      },
    });
  }, [
    excludedAssemblyIds,
    selectedAssemblyIds,
    selectedFilters,
    structureId,
    taskData,
    template?.id,
    updateAllAssemblies,
    updateTaskData,
  ]);

  const handleCloseConfirmationDialog = useCallback(() => {
    setOpenConfirmationDialog(false);
    setOpenTaskModal(true);
  }, [setOpenTaskModal]);

  return (
    <>
      <GenericDialog
        open={openTaskModal}
        onClose={handleClose}
        title="Task"
        content={
          <>
            <Tabs value={tabIndex} onChange={handleTabChange}>
              {TABS.map((tab) => (
                <Tab key={tab.key} label={tab.label} sx={{ textTransform: 'capitalize' }} />
              ))}
            </Tabs>

            <TextField
              autoFocus
              id={TABS[tabIndex].key}
              type="text"
              fullWidth
              multiline
              value={taskData[TABS[tabIndex].key]}
              onChange={(event) => handleInputChange(TABS[tabIndex].key, event?.target.value)}
              rows={15}
              sx={{ mt: 2 }}
              disabled={taskDataLoading}
            />

            <Divider sx={{ mt: 2 }} />
          </>
        }
        actions={
          <Stack
            direction="row"
            justifyContent="flex-end"
            padding="1.6rem 2.4rem"
            borderTop={`1px solid ${abyssColors.primary[100]}`}
            width="100%"
            gap={2}
          >
            <Button sx={textButtonStyle} onClick={handleClose}>
              Cancel
            </Button>
            <Button
              variant="contained"
              disabled={updatingTaskData || taskDataLoading || isSaveDisabled}
              sx={containedButtonStyle}
              onClick={handleSave}
            >
              {updatingTaskData ? (
                <>
                  <CircularProgress sx={{ color: '#fff' }} size="2.4rem" />
                  <span style={{ marginLeft: '10px' }}>Saving</span>
                </>
              ) : (
                <> Save </>
              )}
            </Button>
          </Stack>
        }
      />
      <GenericDialog
        open={openConfirmationDialog}
        onClose={handleCloseConfirmationDialog}
        title="Updating Task Details"
        showCloseIcon
        content={
          <Typography
            fontSize="1.4rem"
            color={abyssColors.primary[700]}
            lineHeight={1.43}
            letterSpacing="normal"
          >
            You have updated the task description. Do you want to apply these changes to only this
            component or to all selected components?
          </Typography>
        }
        actions={
          <Stack
            direction="row"
            justifyContent="flex-end"
            padding="1.6rem 2.4rem"
            borderTop={`1px solid ${abyssColors.primary[100]}`}
            width="100%"
            gap={2}
          >
            <Button sx={textButtonStyle} onClick={handleUpdateAllSelected}>
              Update All Selected
            </Button>
            <Button
              variant="contained"
              disabled={updatingTaskData || taskDataLoading || isSaveDisabled}
              sx={containedButtonStyle}
              onClick={handleUpdateSelectedComponent}
            >
              Update This Component
            </Button>
          </Stack>
        }
      />
    </>
  );
};
